import React, { useState, useEffect, useMemo } from "react";
import {
  Badge,
  Button,
  Label,
  Toast,
  ToastTitle,
  Toaster,
  Tooltip,
  makeStyles,
  tokens,
  useId,
  useToastController,
} from "@fluentui/react-components";
import { ArrowDownload20Filled, Eye16Filled, FilterDismiss20Regular } from "@fluentui/react-icons";
import MaterialReactTable, { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import moment from "moment";
import DynamicDateTimePicker from "../../../components/ControlledDatePicker/dynamicDateTimePicker";
import apiServices from "../../../service";
import { Job, JobFilters } from "./jobManagementTypes";
import JobDetailsModal from "./JobDetailsModal";
import "./styles.css";
import ASCButton from "../../../components/Button";
import { triggerDownload } from "./downloadUtils";

// ── constants ────────────────────────────────────────────────────────────────

const JOB_TYPE_OPTIONS = [
  { label: "All", value: "" },
  { label: "Letter", value: "LETTER_GENERATION" },
  // { label: "Email", value: "EMAIL_GENERATION" },
  // { label: "Report", value: "REPORT" },
];

const JOB_STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Success", value: "success" },
  { label: "In Progress", value: "in_progress" },
  { label: "Partial Success", value: "partial_success" },
  { label: "Failed", value: "failed" },
];

const JOB_TYPE_LABELS: Record<string, string> = {
  LETTER_GENERATION: "Letter",
  EMAIL_GENERATION: "Email",
  REPORT: "Report",
};

const EMPTY_FILTERS: JobFilters = {
  type: "",
  fromDate: "",
  toDate: "",
  status: "",
  consultantId: "",
};

const DEFAULT_PAGINATION: MRT_PaginationState = { pageIndex: 0, pageSize: 10 };

// ── styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  filterContainer: {
    flexShrink: 0,
    padding: "4px 0",
    // Added explicit style to ensure parent does not obscure rendering layers
    position: "relative",
  },
  filterLabel: {
    fontSize: "11px",
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
    lineHeight: "1.2",
    alignSelf: "end",
  },
  filterSelect: {
    height: "32px",
    minWidth: "120px",
    padding: "0 10px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    outline: "none",
    boxSizing: "border-box",
  },
  filterInput: {
    height: "32px",
    minWidth: "130px",
    padding: "0 10px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    boxSizing: "border-box",
    outline: "none",
  },
  actionsGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    height: "32px",
  },
  datePickerWrap: {
    height: "32px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    "& .fui-Field__label": {
      display: "none !important",
    },
    "& .fui-Field": {
      width: "100%",
      height: "32px !important",
      margin: "0 !important",
      padding: "0 !important",
      rowGap: "0 !important",
    },
    "& .react-datepicker-wrapper": {
      width: "100%",
      height: "100%",
    },
    "& .react-datepicker__input-container": {
      height: "100%",
      display: "flex",
      alignItems: "center",
    },
    // Force the actual <input> to the same 32px as the <select> elements.
    // Without this, padding+line-height+border ≈ 34px makes it 2px taller.
    "& .react-datepicker__input-container input": {
      height: "32px !important",
      boxSizing: "border-box",
      marginTop: "0 !important",
    },
  },
});

// ── component ─────────────────────────────────────────────────────────────────

const JobListing: React.FC = () => {
  const styles = useStyles();

  // table data & pagination
  const [jobs, setJobs] = useState<Job[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<MRT_PaginationState>(DEFAULT_PAGINATION);

  // modal
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // download
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  // consultant dropdown options fetched from master data
  const [consultantOptions, setConsultantOptions] = useState<Array<{ value: string; label: string }>>([]);

  // draft filters (bound to filter inputs)
  const [draftType, setDraftType] = useState("");
  const [draftFromDate, setDraftFromDate] = useState<string | null>(null);
  const [draftToDate, setDraftToDate] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState("");
  const [draftConsultant, setDraftConsultant] = useState("");
  const [dateRangeError, setDateRangeError] = useState<string>("");

  // applied filters (submitted to API)
  const [appliedFilters, setAppliedFilters] = useState<JobFilters>(EMPTY_FILTERS);

  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);

  const notifyError = (message: string) =>
    dispatchToast(
      <Toast><ToastTitle>{message}</ToastTitle></Toast>,
      { intent: "error" }
    );

  // ── data fetching ───────────────────────────────────────────────────────────

  const fetchJobs = async (page: MRT_PaginationState, filters: JobFilters) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        offset: page.pageIndex * page.pageSize,
        count: page.pageSize,
        isParent: "true", // only fetch parent jobs for listing
      };
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      if (filters.fromDate) {
        params.fromDate = moment(filters.fromDate).toISOString();
      }
      if (filters.toDate) {
        params.toDate = moment(filters.toDate).toISOString();
      }
      if (filters.consultantId) params.consultantId = filters.consultantId;

      const res = await apiServices.jobManagement.getJobs(params);
      setJobs(res.data?.data ?? []);
      setTotalItems(res.data?.pagination?.totalItems ?? 0);
    } catch {
      notifyError("Failed to load jobs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs(pagination, appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, appliedFilters]);

  useEffect(() => {
    if (draftFromDate && draftToDate && moment(draftFromDate).isAfter(moment(draftToDate))) {
      setDateRangeError("'From' date must not be later than 'To' date.");
      notifyError("From Date cannot be greater than To Date.");
    } else {
      setDateRangeError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftFromDate, draftToDate]);

  useEffect(() => {
    apiServices.selectionList
      .getOptions("consultant_list:MasterDatawithoutNewEntry")
      .then((res) => {
        const fieldOpt = res.data?.data?.find(
          (c: any) => c.field === "consultant_list"
        );
        if (fieldOpt?.options) {
          setConsultantOptions(fieldOpt.options);
        }
      })
      .catch(() => { });
  }, []);

  // ── filter handlers ─────────────────────────────────────────────────────────

  const handleApply = () => {
    if (draftFromDate && draftToDate && moment(draftFromDate).isAfter(moment(draftToDate))) {
      setDateRangeError("'From' date must not be later than 'To' date.");
      return;
    }
    setDateRangeError("");
    setPagination(DEFAULT_PAGINATION);
    setAppliedFilters({
      type: draftType,
      fromDate: draftFromDate ?? "",
      toDate: draftToDate ?? "",
      status: draftStatus,
      consultantId: draftConsultant,
    });
  };

  const hasAppliedFilter =
    appliedFilters.type !== "" ||
    appliedFilters.fromDate !== "" ||
    appliedFilters.toDate !== "" ||
    appliedFilters.status !== "" ||
    appliedFilters.consultantId !== "";

  const draftDiffersFromApplied =
    draftType !== appliedFilters.type ||
    (draftFromDate ?? "") !== appliedFilters.fromDate ||
    (draftToDate ?? "") !== appliedFilters.toDate ||
    draftStatus !== appliedFilters.status ||
    draftConsultant !== appliedFilters.consultantId;

  const hasDraftFilter =
    draftType !== "" ||
    draftFromDate !== null ||
    draftToDate !== null ||
    draftStatus !== "" ||
    draftConsultant !== "";

  const showApply = draftDiffersFromApplied;
  const showClear = hasDraftFilter || hasAppliedFilter;

  const invalidDateRange =
    !!draftFromDate && !!draftToDate && moment(draftFromDate).isAfter(moment(draftToDate));

  const handleDocumentDownload = async (documentId: string) => {
    setDownloadingDocId(documentId);
    try {
      const res = await apiServices.jobManagement.download(documentId);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "application/zip" });
      triggerDownload(blob, `${documentId}.zip`);
    } catch {
      notifyError("Failed to download document.");
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleClear = () => {
    setDraftType("");
    setDraftFromDate(null);
    setDraftToDate(null);
    setDraftStatus("");
    setDraftConsultant("");
    setDateRangeError("");
    setPagination(DEFAULT_PAGINATION);
    setAppliedFilters(EMPTY_FILTERS);
  };

  // ── table columns ───────────────────────────────────────────────────────────

  const columns = useMemo<MRT_ColumnDef<Job>[]>(
    () => [
      {
        id: "completedAt",
        header: "Submitting Date & Time",
        accessorFn: (row) => row.process_data?.completedAt ?? "",
        size: 175,
        Cell: ({ cell }) => {
          const val = cell.getValue<string>();
          return <span>{val ? moment(val).format("DD/MM/YYYY HH:mm") : "—"}</span>;
        },
      },
      {
        id: "type",
        header: "Type",
        accessorFn: (row) => row.process_data?.type ?? "",
        size: 110,
        Cell: ({ cell }) => {
          const val = cell.getValue<string>();
          return <span>{(JOB_TYPE_LABELS[val] ?? val) || "—"}</span>;
        },
      },
      {
        accessorKey: "process_status",
        header: "Status",
        size: 145
      },
      {
        accessorKey: "consultant",
        header: "Consultant",
        size: 140,
      },
      {
        id: "actions",
        header: "Actions",
        size: 110,
        enableSorting: false,
        Cell: ({ row }) => {
          const docId = row.original.process_data?.documentId;
          return (
            <div style={{ display: "flex", gap: "2px" }}>
              <Button
                appearance="subtle"
                size="small"
                icon={<Eye16Filled />}
                title="View details"
                aria-label="View job details"
                onClick={() => {
                  setSelectedJob(row.original);
                  setModalOpen(true);
                }}
              />
              {docId && (
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<ArrowDownload20Filled />}
                  title="Download Zip"
                  aria-label="Download Zip"
                  disabled={downloadingDocId === docId}
                  onClick={() => handleDocumentDownload(docId)}
                />
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="">
      <Toaster toasterId={toasterId} position="top-end" pauseOnHover timeout={3000} />
      <MaterialReactTable<Job>
        columns={columns}
        data={jobs}
        rowCount={totalItems}
        manualPagination
        onPaginationChange={setPagination}
        state={{
          pagination,
          isLoading: loading,
        }}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableColumnActions={false}
        enableFullScreenToggle={false}
        enableDensityToggle={false}
        enableHiding={false}
        enablePinning
        renderToolbarInternalActions={() => null}
        // enableStickyHeader
        initialState={{
          density: "compact",
          columnPinning: { right: ["actions"] },
        }}
        muiTableContainerProps={({ table }) => ({
          sx: {
            height: table.getState().isFullScreen
              ? "calc(100vh - 90px) !important"
              : "calc(100vh - 320px) !important",
          },
        })}
        muiTablePaginationProps={{
          rowsPerPageOptions: [10, 25, 50],
          showFirstButton: false,
          showLastButton: false,
        }}
        muiTableHeadCellProps={{
          sx: {
            backgroundColor: "#f0f0f0",
            color: "#5a6375",
            fontSize: "12px",
            fontWeight: 600,
            // whiteSpace: "nowrap",
            // Keep sticky cells structural depth lower than the body portal view overlays
            zIndex: "1 !important",
          },
        }}
        muiToolbarAlertBannerProps={{ sx: { display: "none" } }}
        localization={{ noRecordsToDisplay: "No records available" }}
        renderTopToolbarCustomActions={() => (
          <div className="table_filter" style={{ minWidth: "fit-content" }}>
            <div
              className={styles.filterContainer}
              style={{
                display: "grid",
                gridAutoFlow: "column",
                gridTemplateRows: "auto 32px",
                columnGap: "12px",
                rowGap: "2px",
                alignItems: "end",
              }}
            >
              {/* ── Type ── */}
              <Label htmlFor="job-type-select">Type</Label>
              <select
                className={styles.filterSelect}
                value={draftType}
                onChange={(e) => setDraftType(e.target.value)}
              >
                {JOB_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              {/* ── From date ── */}
              <Label htmlFor="fromDate">Submitting Date &amp; Time (From)</Label>
              <div className={styles.datePickerWrap}>
                <DynamicDateTimePicker
                  border
                  name="fromDate"
                  required={false}
                  disabled={false}
                  width="230px"
                  formik={{
                    values: { fromDate: draftFromDate },
                    setFieldValue: (_: string, v: string | null) => {
                      setDraftFromDate(v);
                    },
                    setFieldError: () => { },
                    errors: {},
                    touched: {},
                  }}
                />
              </div>

              {/* ── To date ── */}
              <Label htmlFor="toDate">Submitting Date &amp; Time (To)</Label>
              <div className={styles.datePickerWrap}>
                <DynamicDateTimePicker
                  border
                  name="toDate"
                  required={false}
                  disabled={false}
                  width="230px"
                  formik={{
                    values: { toDate: draftToDate },
                    setFieldValue: (_: string, v: string | null) => {
                      setDraftToDate(v);
                    },
                    setFieldError: () => { },
                    errors: {},
                    touched: {},
                  }}
                />
              </div>

              {/* ── Status ── */}
              <Label htmlFor="job-status-select">Status</Label>
              <select
                className={styles.filterSelect}
                value={draftStatus}
                onChange={(e) => setDraftStatus(e.target.value)}
              >
                {JOB_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              {/* ── Consultant ── */}
              <Label htmlFor="job-consultant-select">Consultant</Label>
              <select
                className={styles.filterSelect}
                value={draftConsultant}
                onChange={(e) => setDraftConsultant(e.target.value)}
              >
                <option value="">All</option>
                {consultantOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              {/* ── Buttons ── */}
              <span />
              <div className={styles.actionsGroup}>
                {showApply && (
                  // <Tooltip
                  //   content={invalidDateRange ? "'From' date must not be later than 'To' date." : "Apply filter"}
                  //   relationship="label"
                  // >
                  <ASCButton
                    appearance="secondary"
                    label="Apply"
                    disabled={loading || invalidDateRange}
                    style={{ height: "32px", borderRadius: tokens.borderRadiusMedium, backgroundColor: invalidDateRange ? undefined : "#205493ef", color: invalidDateRange ? undefined : "#fff" }}
                    onClick={handleApply}
                  />
                  // </Tooltip>
                )}
                {showClear && (
                  <Tooltip content="Clear filter" relationship="label">
                    <Button
                      style={{ height: "32px", minWidth: "32px" }}
                      icon={<FilterDismiss20Regular />}
                      onClick={handleClear}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        )}
      />
      <JobDetailsModal
        job={selectedJob}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

export default JobListing;